import React, { useMemo, useState } from 'react';
import { Box, Collapse, useTheme } from '@mui/material';
import { diffWordsSegments, diffLinesForSideBySide, splitEqualRunForCollapse } from './agentDiff';
import { renderDiffText } from './renderDiffText';

const TOGGLE_SHOW_SX = { bgcolor: '#eef2ff', color: '#4338ca', borderColor: '#c7d2fe', '&:hover': { bgcolor: '#e0e7ff', borderColor: '#a5b4fc' } };
const TOGGLE_HIDE_SX = { bgcolor: '#f1f5f9', color: '#64748b', borderColor: '#e2e8f0', '&:hover': { bgcolor: '#e2e8f0', borderColor: '#cbd5e1' } };

// Shared red/green add-remove tint, used for both inline <ins>/<del> spans
// and side-by-side cells.
function tintSx(theme, isInsert) {
  return {
    bgcolor: `${isInsert ? theme.palette.success.main : theme.palette.error.main}22`,
    color: isInsert ? 'success.dark' : 'error.dark',
    borderRadius: 0.5,
  };
}

function CollapseToggle({ expanded, label, onClick }) {
  return (
    <Box
      component="span"
      onClick={onClick}
      sx={{
        ...(expanded ? TOGGLE_HIDE_SX : TOGGLE_SHOW_SX),
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        border: '1.5px solid',
        borderRadius: 999,
        px: 1.5,
        py: 0.25,
        mx: 0.25,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color .1s, border-color .1s',
      }}
    >
      <Box component="span" sx={{ fontSize: 10 }}>{expanded ? '▾' : '▸'}</Box>
      {expanded ? 'Hide' : label}
    </Box>
  );
}

function toggleInSet(setter, key) {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
}

function EqualRun({ text, runKey, expandedRuns, setExpandedRuns }) {
  const split = splitEqualRunForCollapse(text);
  if (!split.collapsible) return <>{renderDiffText(split.text)}</>;
  const expanded = expandedRuns.has(runKey);
  return (
    <>
      {renderDiffText(split.head)}
      <CollapseToggle
        expanded={expanded}
        label={`Show ${split.wordCount} unchanged words`}
        onClick={() => toggleInSet(setExpandedRuns, runKey)}
      />
      {expanded && renderDiffText(split.middle)}
      {renderDiffText(split.tail)}
    </>
  );
}

function InlineSegment({ segment, index, theme, expandedRuns, setExpandedRuns }) {
  if (segment.type === 'equal') {
    return (
      <EqualRun text={segment.value} runKey={index} expandedRuns={expandedRuns} setExpandedRuns={setExpandedRuns} />
    );
  }
  const isInsert = segment.type === 'insert';
  return (
    <Box component={isInsert ? 'ins' : 'del'} sx={{ textDecoration: 'none', px: '2px', ...tintSx(theme, isInsert) }}>
      {renderDiffText(segment.value)}
    </Box>
  );
}

function InlineDiff({ oldText, newText }) {
  const theme = useTheme();
  const [expandedRuns, setExpandedRuns] = useState(() => new Set());
  const segments = useMemo(() => diffWordsSegments(oldText, newText), [oldText, newText]);
  return (
    <Box
      component="pre"
      sx={{ m: 0, fontFamily: 'inherit', fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
    >
      {segments.map((segment, i) => (
        <InlineSegment
          key={i}
          segment={segment}
          index={i}
          theme={theme}
          expandedRuns={expandedRuns}
          setExpandedRuns={setExpandedRuns}
        />
      ))}
    </Box>
  );
}

const cellSx = { fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' };

function renderWordDiffSide(segments, side, theme) {
  return segments.map((segment, i) => {
    if (segment.type === 'equal') {
      return <React.Fragment key={i}>{renderDiffText(segment.value)}</React.Fragment>;
    }
    if (segment.type === 'delete' && side === 'old') {
      return (
        <Box key={i} component="span" sx={{ px: '2px', ...tintSx(theme, false) }}>{renderDiffText(segment.value)}</Box>
      );
    }
    if (segment.type === 'insert' && side === 'new') {
      return (
        <Box key={i} component="span" sx={{ px: '2px', ...tintSx(theme, true) }}>{renderDiffText(segment.value)}</Box>
      );
    }
    return null;
  });
}

function CollapsedLinesRow({ lines, count, rowKey, expandedRuns, setExpandedRuns }) {
  const expanded = expandedRuns.has(rowKey);
  const cell = (
    <Box sx={cellSx}>
      <CollapseToggle
        expanded={expanded}
        label={`Show ${count} unchanged lines`}
        onClick={() => toggleInSet(setExpandedRuns, rowKey)}
      />
      <Collapse in={expanded}>{renderDiffText(lines.join('\n'))}</Collapse>
    </Box>
  );
  return (
    <>
      {cell}
      {cell}
    </>
  );
}

function SideBySideDiff({ oldText, newText }) {
  const theme = useTheme();
  const [expandedRuns, setExpandedRuns] = useState(() => new Set());
  const rows = useMemo(() => diffLinesForSideBySide(oldText, newText), [oldText, newText]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '1px', bgcolor: 'divider' }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px', alignItems: 'start' }}>
      {rows.map((row, i) => {
        if (row.kind === 'equal') {
          return (
            <React.Fragment key={i}>
              <Box sx={cellSx}>{renderDiffText(row.oldLine)}</Box>
              <Box sx={cellSx}>{renderDiffText(row.newLine)}</Box>
            </React.Fragment>
          );
        }
        if (row.kind === 'collapsed') {
          return (
            <CollapsedLinesRow
              key={i}
              lines={row.lines}
              count={row.count}
              rowKey={i}
              expandedRuns={expandedRuns}
              setExpandedRuns={setExpandedRuns}
            />
          );
        }
        if (row.kind === 'removed') {
          return (
            <React.Fragment key={i}>
              <Box sx={{ ...cellSx, ...tintSx(theme, false), px: 0.5 }}>{renderDiffText(row.oldLine)}</Box>
              <Box sx={cellSx} />
            </React.Fragment>
          );
        }
        if (row.kind === 'added') {
          return (
            <React.Fragment key={i}>
              <Box sx={cellSx} />
              <Box sx={{ ...cellSx, ...tintSx(theme, true), px: 0.5 }}>{renderDiffText(row.newLine)}</Box>
            </React.Fragment>
          );
        }
        // 'changed'
        return (
          <React.Fragment key={i}>
            <Box sx={cellSx}>{renderWordDiffSide(row.wordDiff, 'old', theme)}</Box>
            <Box sx={cellSx}>{renderWordDiffSide(row.wordDiff, 'new', theme)}</Box>
          </React.Fragment>
        );
      })}
      </Box>
    </Box>
  );
}

export default function NodeContentDiff({ oldText, newText, viewMode }) {
  if (viewMode === 'side-by-side') return <SideBySideDiff oldText={oldText} newText={newText} />;
  return <InlineDiff oldText={oldText} newText={newText} />;
}
